import * as React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Tooltip,
  IconButton,
  Button,
  Link,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import { GenericDialog } from "app/components/Alerts/GenericDialog";
import { SharedService } from "app/services/SharedServices/shared-service";
import type { ScenarioHistoryRow, DashboardModel } from "./kpiEngine";
import { buildDashboardModel } from "./kpiEngine";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface ScenarioHistoryModalProps {
  open: boolean;
  onClose: () => void;
  scenarioId: number;
}

export const ScenarioHistoryModal: React.FC<ScenarioHistoryModalProps> = ({
  open,
  onClose,
  scenarioId,
}) => {
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState<DashboardModel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [similarOrders, setSimilarOrders] = useState<Array<{ orderId: number; similarityScore: number }>>([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [errorSimilar, setErrorSimilar] = useState<string | null>(null);

  const fetchScenarioHistory = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const service = SharedService.getInstance();
      const data = await service.getScenarioHistoryDetails(scenarioId);
      
      // Transform API response to ScenarioHistoryRow format
      const transformedData: ScenarioHistoryRow[] = data.map((item: any) => ({
        id: item.id,
        orderId: item.orderId,
        bookingPeriodId: item.bookingPeriodId,
        orderPayPackageId: item.orderPayPackageId,
        createdDate: item.createdDate || item.lastUpdatedDate || new Date().toISOString(),
        createdById: item.createdById,
        startDate: item.startDate || new Date().toISOString(),
        endDate: item.endDate || new Date().toISOString(),
        weeklyGrossPay: item.weeklyGrossPay,
        minimumWeeklyGrossPay: item.minimumWeeklyGrossPay,
        maximumWeeklyGrossPay: item.maximumWeeklyGrossPay,
        targetMargin: item.targetMargin,
        negotiatedContributionMargin: item.negotiatedContributionMargin,
        revenue: item.revenue,
        grossProfit: item.grossProfit,
        grossMargin: item.grossMargin,
        // Pass through any other fields via index signature
        ...item,
      }));

      const dashboardModel = buildDashboardModel(transformedData);
      setDashboard(dashboardModel);
    } catch (err: any) {
      console.error("Error fetching scenario history:", err);
      setError(err?.message || "Failed to load scenario history");
    } finally {
      setLoading(false);
    }
  }, [scenarioId]);

  useEffect(() => {
    if (open && scenarioId) {
      fetchScenarioHistory();
    }
  }, [open, scenarioId, fetchScenarioHistory]);

  // Color schemes for different chart types
  const colors = {
    profit: "#2E7D32", // Green
    pay: "#1976D2", // Blue
    payBand: "#FF9800", // Orange
    flexibility: "#9C27B0", // Purple
    positive: "#4CAF50", // Green
    negative: "#F44336", // Red
    neutral: "#FF9800", // Orange
    min: "#81C784", // Light green
    max: "#FFB74D", // Light orange
    rates: "#E91E63", // Pink
    financial: "#00BCD4", // Cyan
    costs: "#795548", // Brown
  };

  // Group time series by metric name for individual charts
  const timeSeriesByMetric = dashboard?.timeSeries.reduce((acc, point) => {
    if (!acc[point.metricName]) {
      acc[point.metricName] = [];
    }
    acc[point.metricName].push({
      date: point.metricDate,
      value: point.value,
    });
    return acc;
  }, {} as Record<string, Array<{ date: string; value: number }>>) || {};

  const formatValue = (value: number, metricName: string): string => {
    // Handle percentage metrics
    if (metricName.includes('%') || metricName.includes('Rate') || metricName.includes('Margin') || metricName.includes('Position')) {
      return `${value.toFixed(2)}%`;
    }
    // Handle day-based metrics
    if (metricName.includes('Days') || metricName.includes('Longevity')) {
      return `${Math.round(value)}`;
    }
    // Handle currency metrics
    if (metricName.includes('Pay') || metricName.includes('Width') || metricName.includes('Change')) {
      return `$${value.toFixed(2)}`;
    }
    // Handle boolean/yes-no metrics (value is 0 or 1)
    if (metricName.includes('Stabilised')) {
      return value === 1 ? 'Yes' : 'No';
    }
    // Default: round integers, show decimals for others
    return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  };

  // Tooltip content for each section
  const sectionTooltips = {
    businessInsights: (
      <Box sx={{ p: 1, maxWidth: 400 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>Business Insights</strong>
        </Typography>
        <Typography variant="body2" paragraph>
          This section answers key business questions about your order pricing strategy:
        </Typography>
        <Typography variant="body2" component="div">
          <strong>Formulas:</strong>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>Margin Change = Final Margin - Initial Margin</li>
            <li>Pay Pressure = (Pay Increased) AND (Margin Decreased)</li>
            <li>Stability = Count consecutive saves with no changes (≥3 = stabilized)</li>
            <li>Band Realism = Pay Range ≤ Band Width × 1.5</li>
            <li>Negotiation Dependency = (Saves with negotiation / Total saves) × 100</li>
            <li>Competitiveness = Pay Band Position between 40-60%</li>
            <li>Profitability = Final Margin ≥ Initial Margin × 0.95</li>
          </ul>
        </Typography>
      </Box>
    ),
    comparison: (
      <Box sx={{ p: 1, maxWidth: 400 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>Before & After Comparison</strong>
        </Typography>
        <Typography variant="body2" paragraph>
          Shows side-by-side comparison of initial vs final values for key metrics.
        </Typography>
        <Typography variant="body2" component="div">
          <strong>Formulas:</strong>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>Change = Final Value - Initial Value</li>
            <li>Change % = (Change / Initial Value) × 100</li>
            <li>Initial = Value from first save (oldest)</li>
            <li>Final = Value from last save (newest)</li>
          </ul>
        </Typography>
      </Box>
    ),
    payBandAnalysis: (
      <Box sx={{ p: 1, maxWidth: 400 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>Pay Band Analysis</strong>
        </Typography>
        <Typography variant="body2" paragraph>
          Visualizes how your actual pay compares to the allowed pay range (min to max) over time.
        </Typography>
        <Typography variant="body2" component="div">
          <strong>Formulas:</strong>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>Band Width = Maximum Pay - Minimum Pay (from last row)</li>
            <li>Min Pay = minimumWeeklyGrossPay from each save</li>
            <li>Max Pay = maximumWeeklyGrossPay from each save</li>
            <li>Actual Pay = weeklyGrossPay from each save</li>
          </ul>
        </Typography>
      </Box>
    ),
    marginVsPay: (
      <Box sx={{ p: 1, maxWidth: 400 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>Margin vs Pay Relationship</strong>
        </Typography>
        <Typography variant="body2" paragraph>
          Analyzes the relationship between target margin and weekly gross pay to identify if pay increases are affecting margins.
        </Typography>
        <Typography variant="body2" component="div">
          <strong>Formulas:</strong>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>Target Margin = targetMargin from each save</li>
            <li>Weekly Gross Pay = weeklyGrossPay from each save</li>
            <li>Pay Pushed Margin Down = (Pay Increased) AND (Margin Decreased)</li>
          </ul>
        </Typography>
      </Box>
    ),
    rateAnalysis: (
      <Box sx={{ p: 1, maxWidth: 400 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>Rate Analysis</strong>
        </Typography>
        <Typography variant="body2" paragraph>
          Compares bill rate (what you charge) vs pay rate (what you pay) and calculates the spread (profit per hour).
        </Typography>
        <Typography variant="body2" component="div">
          <strong>Formulas:</strong>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>Bill Rate = billRate from each save</li>
            <li>Pay Rate = payRate from each save</li>
            <li>Spread = Bill Rate - Pay Rate</li>
            <li>Initial Spread = Initial Bill Rate - Initial Pay Rate</li>
            <li>Final Spread = Final Bill Rate - Final Pay Rate</li>
          </ul>
        </Typography>
      </Box>
    ),
    financialPerformance: (
      <Box sx={{ p: 1, maxWidth: 400 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>Financial Performance</strong>
        </Typography>
        <Typography variant="body2" paragraph>
          Tracks revenue, gross profit, and gross margin over time to assess financial health.
        </Typography>
        <Typography variant="body2" component="div">
          <strong>Formulas:</strong>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>Revenue = revenue from each save</li>
            <li>Gross Profit = grossProfit from each save</li>
            <li>Gross Margin = grossMargin × 100 (converted to percentage)</li>
            <li>Gross Margin % = (Gross Profit / Revenue) × 100</li>
          </ul>
        </Typography>
      </Box>
    ),
    costComponents: (
      <Box sx={{ p: 1, maxWidth: 400 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>Cost Components Analysis</strong>
        </Typography>
        <Typography variant="body2" paragraph>
          Breaks down different cost types (VMS, Other, Meals, Lodging) to identify cost drivers.
        </Typography>
        <Typography variant="body2" component="div">
          <strong>Formulas:</strong>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>VMS = vms from each save</li>
            <li>Other Costs = other from each save</li>
            <li>Meals Per Diem = mealsAndIncidentalsPerDiem from each save</li>
            <li>Lodging Per Diem = lodgingPerDiem from each save</li>
            <li>Average = Sum of all values / Count of non-null values</li>
          </ul>
        </Typography>
      </Box>
    ),
    additionalMetrics: (
      <Box sx={{ p: 1, maxWidth: 400 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>Additional Metrics</strong>
        </Typography>
        <Typography variant="body2" paragraph>
          Shows other time series metrics that don't fit into the main categories.
        </Typography>
        <Typography variant="body2" component="div">
          <strong>Available Metrics:</strong>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>Pay Band Position (%) = ((Weekly Pay - Min Pay) / (Max Pay - Min Pay)) × 100</li>
            <li>All metrics are calculated from the latest 25 saves</li>
          </ul>
        </Typography>
      </Box>
    ),
    historyDetails: (
      <Box sx={{ p: 1, maxWidth: 400 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>History Details</strong>
        </Typography>
        <Typography variant="body2" paragraph>
          Raw data table showing all saves with key metrics. Data is sorted from newest to oldest.
        </Typography>
        <Typography variant="body2" component="div">
          <strong>Data Source:</strong>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>Shows latest 25 saves (limited for performance)</li>
            <li>Sorted by createdDate (newest first in table)</li>
          </ul>
        </Typography>
      </Box>
    ),
    payBandRange: (
      <Box sx={{ p: 1, maxWidth: 400 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>Pay Band Range Over Time</strong>
        </Typography>
        <Typography variant="body2" paragraph>
          Shows the minimum and maximum pay boundaries (shaded areas) along with your actual weekly gross pay (line) over time.
        </Typography>
        <Typography variant="body2" component="div">
          <strong>Data Points:</strong>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>Min Pay = minimumWeeklyGrossPay from each save</li>
            <li>Max Pay = maximumWeeklyGrossPay from each save</li>
            <li>Actual Pay = weeklyGrossPay from each save</li>
            <li>Band Width = Max Pay - Min Pay (from last row)</li>
          </ul>
        </Typography>
      </Box>
    ),
    payPushMargin: (
      <Box sx={{ p: 1, maxWidth: 400 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>Did Pay Push Margin Down?</strong>
        </Typography>
        <Typography variant="body2" paragraph>
          Analyzes if increases in weekly gross pay coincided with decreases in target margin.
        </Typography>
        <Typography variant="body2" component="div">
          <strong>Formula:</strong>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>Pay Pushed Margin Down = (Pay Increased) AND (Margin Decreased)</li>
            <li>Pay Increased = Final Pay &gt; Initial Pay</li>
            <li>Margin Decreased = Final Margin &lt; Initial Margin</li>
          </ul>
        </Typography>
      </Box>
    ),
    billPayRateComparison: (
      <Box sx={{ p: 1, maxWidth: 400 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>Bill Rate vs Pay Rate Comparison</strong>
        </Typography>
        <Typography variant="body2" paragraph>
          Compares what you charge clients (bill rate) vs what you pay workers (pay rate) and shows the spread (profit per hour).
        </Typography>
        <Typography variant="body2" component="div">
          <strong>Formulas:</strong>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>Spread = Bill Rate - Pay Rate</li>
            <li>Bill Rate = billRate from each save</li>
            <li>Pay Rate = payRate from each save</li>
            <li>Larger spread = More profit per hour</li>
          </ul>
        </Typography>
      </Box>
    ),
    revenueVsProfit: (
      <Box sx={{ p: 1, maxWidth: 400 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>Revenue vs Gross Profit</strong>
        </Typography>
        <Typography variant="body2" paragraph>
          Compares total revenue (shaded area) with gross profit (line) to assess if profit is growing with revenue.
        </Typography>
        <Typography variant="body2" component="div">
          <strong>Formulas:</strong>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>Revenue = revenue from each save</li>
            <li>Gross Profit = grossProfit from each save</li>
            <li>Profit Margin = (Gross Profit / Revenue) × 100</li>
          </ul>
        </Typography>
      </Box>
    ),
    grossMarginTrend: (
      <Box sx={{ p: 1, maxWidth: 400 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>Gross Margin Trend</strong>
        </Typography>
        <Typography variant="body2" paragraph>
          Shows your profit margin percentage over time. Upward trend indicates improving profitability.
        </Typography>
        <Typography variant="body2" component="div">
          <strong>Formula:</strong>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>Gross Margin = grossMargin × 100 (converted to percentage)</li>
            <li>Gross Margin % = (Gross Profit / Revenue) × 100</li>
            <li>Higher margin = Better profitability</li>
          </ul>
        </Typography>
      </Box>
    ),
    costComponentsOverTime: (
      <Box sx={{ p: 1, maxWidth: 400 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>Cost Components Over Time</strong>
        </Typography>
        <Typography variant="body2" paragraph>
          Breaks down different cost types (VMS, Other, Meals, Lodging) to identify which costs are highest and track trends.
        </Typography>
        <Typography variant="body2" component="div">
          <strong>Data Points:</strong>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>VMS = vms from each save</li>
            <li>Other Costs = other from each save</li>
            <li>Meals Per Diem = mealsAndIncidentalsPerDiem from each save</li>
            <li>Lodging Per Diem = lodgingPerDiem from each save</li>
          </ul>
        </Typography>
      </Box>
    ),
  };

  return (
    <GenericDialog
      open={open}
      onClose={onClose}
      variant="blue"
      dialogTitleProps={{
        text: "Margin Scenario History",
        closeButton: true,
        expandable: true,
      }}
      dialogContentProps={{
        sx: { minHeight: '400px', maxHeight: '80vh', overflow: 'auto' }
      }}
      maxWidth="lg"
      fullWidth
    >
      {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box p={2}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {!loading && !error && dashboard && dashboard.kpis.length > 0 && (
          <Box>
            {/* Business Insights Section */}
            {dashboard.insights && dashboard.insights.length > 0 && (
              <>
                <Box display="flex" alignItems="center" sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="h6" sx={{ mr: 1 }}>
                    Business Insights
                  </Typography>
                  <Tooltip title={sectionTooltips.businessInsights} arrow placement="right">
                    <IconButton size="small" sx={{ color: 'info.main' }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  {dashboard.insights.map((insight, index) => {
                    // Tooltip content for each insight
                    const getInsightTooltip = (question: string) => {
                      if (question.includes('margin hold or erode')) {
                        return (
                          <Box sx={{ p: 1, maxWidth: 400 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              <strong>Did Margin Hold or Erode?</strong>
                            </Typography>
                            <Typography variant="body2" paragraph>
                              Compares initial vs final target margin to see if profitability improved or declined.
                            </Typography>
                            <Typography variant="body2" component="div">
                              <strong>Formula:</strong>
                              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                                <li>Margin Change = Final Margin - Initial Margin</li>
                                <li>Initial Margin = targetMargin from first save</li>
                                <li>Final Margin = targetMargin from last save</li>
                                <li>Positive = Margin improved or held</li>
                                <li>Negative = Margin eroded</li>
                              </ul>
                            </Typography>
                          </Box>
                        );
                      }
                      if (question.includes('pay push margin down')) {
                        return (
                          <Box sx={{ p: 1, maxWidth: 400 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              <strong>Did Pay Push Margin Down?</strong>
                            </Typography>
                            <Typography variant="body2" paragraph>
                              Analyzes if pay increases caused margin decreases, indicating pay pressure on profitability.
                            </Typography>
                            <Typography variant="body2" component="div">
                              <strong>Formula:</strong>
                              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                                <li>Pay Pushed Margin Down = (Pay Increased) AND (Margin Decreased)</li>
                                <li>Pay Increased = Final Pay &gt; Initial Pay</li>
                                <li>Margin Decreased = Final Margin &lt; Initial Margin</li>
                              </ul>
                            </Typography>
                          </Box>
                        );
                      }
                      if (question.includes('scenario stabilise')) {
                        return (
                          <Box sx={{ p: 1, maxWidth: 400 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              <strong>Did the Scenario Stabilise?</strong>
                            </Typography>
                            <Typography variant="body2" paragraph>
                              Checks if you stopped making changes and settled on final pricing (3+ consecutive saves with no changes).
                            </Typography>
                            <Typography variant="body2" component="div">
                              <strong>Formula:</strong>
                              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                                <li>Count consecutive saves from end with no changes</li>
                                <li>Stabilized = 3+ consecutive saves unchanged</li>
                                <li>Checks both weeklyGrossPay and targetMargin</li>
                              </ul>
                            </Typography>
                          </Box>
                        );
                      }
                      if (question.includes('pay band realistic')) {
                        return (
                          <Box sx={{ p: 1, maxWidth: 400 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              <strong>Was the Pay Band Realistic?</strong>
                            </Typography>
                            <Typography variant="body2" paragraph>
                              Checks if the pay band width was sufficient to accommodate actual pay variations.
                            </Typography>
                            <Typography variant="body2" component="div">
                              <strong>Formula:</strong>
                              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                                <li>Band Width = Max Pay - Min Pay (from last row)</li>
                                <li>Pay Range = MAX(weeklyGrossPay) - MIN(weeklyGrossPay)</li>
                                <li>Realistic = Pay Range ≤ Band Width × 1.5</li>
                                <li>If Pay Range &gt; Band Width × 1.5, band is too narrow</li>
                              </ul>
                            </Typography>
                          </Box>
                        );
                      }
                      if (question.includes('defaults causing negotiation')) {
                        return (
                          <Box sx={{ p: 1, maxWidth: 400 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              <strong>Are Defaults Causing Negotiation?</strong>
                            </Typography>
                            <Typography variant="body2" paragraph>
                              Checks if you're relying too much on default settings instead of negotiating.
                            </Typography>
                            <Typography variant="body2" component="div">
                              <strong>Formula:</strong>
                              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                                <li>Default Ratio = (Saves without negotiation / Total saves) × 100</li>
                                <li>Negotiation = negotiatedContributionMargin is not null</li>
                                <li>If Default Ratio &gt; 50%, defaults are causing issues</li>
                              </ul>
                            </Typography>
                          </Box>
                        );
                      }
                      if (question.includes('competitive but profitable')) {
                        return (
                          <Box sx={{ p: 1, maxWidth: 400 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              <strong>Is the Posting Competitive but Profitable?</strong>
                            </Typography>
                            <Typography variant="body2" paragraph>
                              Checks if you're positioned competitively (middle of pay band) while maintaining profitability.
                            </Typography>
                            <Typography variant="body2" component="div">
                              <strong>Formulas:</strong>
                              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                                <li>Competitive = Pay Band Position between 40-60%</li>
                                <li>Profitable = Final Margin ≥ Initial Margin × 0.95</li>
                                <li>Pay Band Position = ((Pay - Min Pay) / (Max Pay - Min Pay)) × 100</li>
                              </ul>
                            </Typography>
                          </Box>
                        );
                      }
                      return null;
                    };

                    return (
                      <Grid item xs={12} md={6} key={index}>
                        <Alert
                          severity={
                            insight.status === 'positive' ? 'success' :
                            insight.status === 'negative' ? 'error' : 'warning'
                          }
                          icon={
                            insight.status === 'positive' ? <CheckCircleIcon /> :
                            insight.status === 'negative' ? <ErrorIcon /> : <WarningIcon />
                          }
                          sx={{ height: '100%' }}
                        >
                          <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
                              {insight.question}
                            </Typography>
                            {getInsightTooltip(insight.question) && (
                              <Tooltip title={getInsightTooltip(insight.question)} arrow placement="right">
                                <IconButton size="small" sx={{ color: 'info.main', ml: 0.5, p: 0.5 }}>
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          <Typography variant="body2">
                            {insight.answer}
                          </Typography>
                          {insight.display && (
                            <Chip
                              label={insight.display}
                              size="small"
                              sx={{
                                mt: 1,
                                backgroundColor:
                                  insight.status === 'positive' ? colors.positive :
                                  insight.status === 'negative' ? colors.negative : colors.neutral,
                                color: 'white',
                              }}
                            />
                          )}
                        </Alert>
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            )}

            {/* Comparison Section */}
            {dashboard.comparisons && dashboard.comparisons.length > 0 && (
              <>
                <Box display="flex" alignItems="center" sx={{ mt: 4, mb: 2 }}>
                  <Typography variant="h6" sx={{ mr: 1 }}>
                    Before & After Comparison
                  </Typography>
                  <Tooltip title={sectionTooltips.comparison} arrow placement="right">
                    <IconButton size="small" sx={{ color: 'info.main' }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  {dashboard.comparisons.map((comp, index) => {
                    // Tooltip content for each comparison
                    const getComparisonTooltip = (label: string) => {
                      return (
                        <Box sx={{ p: 1, maxWidth: 400 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            <strong>{label}</strong>
                          </Typography>
                          <Typography variant="body2" paragraph>
                            Shows initial vs final value comparison with change amount and percentage.
                          </Typography>
                          <Typography variant="body2" component="div">
                            <strong>Formulas:</strong>
                            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                              <li>Initial = Value from first save (oldest)</li>
                              <li>Final = Value from last save (newest)</li>
                              <li>Change = Final - Initial</li>
                              <li>Change % = (Change / Initial) × 100</li>
                            </ul>
                          </Typography>
                        </Box>
                      );
                    };

                    return (
                      <Grid item xs={12} md={6} key={index}>
                        <Paper elevation={1} sx={{ p: 2 }}>
                          <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ mr: 1, flex: 1 }}>
                              {comp.label}
                            </Typography>
                            <Tooltip title={getComparisonTooltip(comp.label)} arrow placement="right">
                              <IconButton size="small" sx={{ color: 'info.main' }}>
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={[
                              { name: 'Initial', value: comp.initial, fill: colors.negative },
                              { name: 'Final', value: comp.final, fill: colors.positive },
                            ]}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <RechartsTooltip />
                              <Bar dataKey="value" />
                            </BarChart>
                          </ResponsiveContainer>
                          <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Chip
                              label={`${comp.change >= 0 ? '+' : ''}${comp.change.toFixed(2)} (${comp.changePct >= 0 ? '+' : ''}${comp.changePct.toFixed(1)}%)`}
                              color={comp.change >= 0 ? 'success' : 'error'}
                              size="small"
                            />
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            )}

            {/* KPI Section */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
              Key Performance Indicators
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {dashboard.kpis.map((kpi, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      borderLeft: `4px solid ${
                        kpi.metricGroup === 'Profit Outcome' ? colors.profit :
                        kpi.metricGroup === 'Pay Pressure' ? colors.pay :
                        kpi.metricGroup === 'Stability' ? colors.neutral :
                        kpi.metricGroup === 'Flexibility' ? colors.flexibility :
                        '#757575'
                      }`,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {kpi.metricGroup} - {kpi.metricName}
                    </Typography>
                    <Typography variant="h5" sx={{ mt: 1 }}>
                      {kpi.display || formatValue(kpi.value, kpi.metricName)}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Find Similar Orders Section */}
            <Box sx={{ mt: 4, mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Find Similar Orders
              </Typography>
              <Box sx={{ maxWidth: 600 }}>
                <Paper elevation={1} sx={{ p: 2, minHeight: 200 }}>
                  {similarOrders.length === 0 && !loadingSimilar && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 150 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                          setLoadingSimilar(true);
                          setErrorSimilar(null);
                          try {
                            const service = SharedService.getInstance();
                            const data = await service.getScenarioHistorySimilarDetails(scenarioId);
                            setSimilarOrders(data || []);
                          } catch (err: any) {
                            console.error("Error fetching similar orders:", err);
                            setErrorSimilar(err?.message || "Failed to load similar orders");
                          } finally {
                            setLoadingSimilar(false);
                          }
                        }}
                      >
                        Fetch Similar Orders
                      </Button>
                    </Box>
                  )}
                  {loadingSimilar && (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={150}>
                      <CircularProgress />
                    </Box>
                  )}
                  {errorSimilar && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {errorSimilar}
                    </Alert>
                  )}
                  {similarOrders.length > 0 && !loadingSimilar && (
                    <>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Order ID</TableCell>
                              <TableCell align="right">Similarity Score</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {similarOrders.map((order, index) => (
                              <TableRow key={index} hover>
                                <TableCell>
                                  <Link
                                    href={`/order/${order.orderId}`}
                                    data-testid="orders-orderid-link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ textDecoration: 'none' }}
                                  >
                                    {order.orderId}
                                  </Link>
                                </TableCell>
                                <TableCell align="right">
                                  {order.similarityScore.toFixed(2)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setSimilarOrders([]);
                            setErrorSimilar(null);
                          }}
                        >
                          Clear Results
                        </Button>
                      </Box>
                    </>
                  )}
                </Paper>
              </Box>
            </Box>

            {/* Time Series Charts - Combined Pay Band Chart */}
            {timeSeriesByMetric['Weekly Gross Pay Over Time'] && 
             timeSeriesByMetric['Min Weekly Gross Pay'] && 
             timeSeriesByMetric['Max Weekly Gross Pay'] && (
              <>
                <Box display="flex" alignItems="center" sx={{ mt: 4, mb: 2 }}>
                  <Typography variant="h6" sx={{ mr: 1 }}>
                    Pay Band Analysis
                  </Typography>
                  <Tooltip title={sectionTooltips.payBandAnalysis} arrow placement="right">
                    <IconButton size="small" sx={{ color: 'info.main' }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12}>
                    <Paper elevation={1} sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ mr: 1 }}>
                          Pay Band Range Over Time
                        </Typography>
                        <Tooltip title={sectionTooltips.payBandRange} arrow placement="right">
                          <IconButton size="small" sx={{ color: 'info.main' }}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <ResponsiveContainer width="100%" height={350}>
                        <ComposedChart
                          data={timeSeriesByMetric['Weekly Gross Pay Over Time'].map((item, idx) => ({
                            date: item.date,
                            pay: item.value,
                            minPay: timeSeriesByMetric['Min Weekly Gross Pay'][idx]?.value || 0,
                            maxPay: timeSeriesByMetric['Max Weekly Gross Pay'][idx]?.value || 0,
                          }))}
                        >
                          <defs>
                            <linearGradient id="colorPayBand" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={colors.payBand} stopOpacity={0.3} />
                              <stop offset="95%" stopColor={colors.payBand} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 12 }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                          <YAxis tick={{ fontSize: 12 }} />
                          <RechartsTooltip />
                          <Legend />
                          <Area
                            type="monotone"
                            dataKey="minPay"
                            stroke={colors.min}
                            fill={colors.min}
                            fillOpacity={0.2}
                            name="Min Pay"
                          />
                          <Area
                            type="monotone"
                            dataKey="maxPay"
                            stroke={colors.max}
                            fill={colors.max}
                            fillOpacity={0.2}
                            name="Max Pay"
                          />
                          <Line
                            type="monotone"
                            dataKey="pay"
                            stroke={colors.pay}
                            strokeWidth={3}
                            dot={{ r: 5 }}
                            name="Weekly Gross Pay"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                </Grid>
              </>
            )}

            {/* Time Series Charts - Margin vs Pay Comparison */}
            {timeSeriesByMetric['Target Margin Over Time'] && timeSeriesByMetric['Weekly Gross Pay Over Time'] && (
              <>
                <Box display="flex" alignItems="center" sx={{ mt: 4, mb: 2 }}>
                  <Typography variant="h6" sx={{ mr: 1 }}>
                    Margin vs Pay Relationship
                  </Typography>
                  <Tooltip title={sectionTooltips.marginVsPay} arrow placement="right">
                    <IconButton size="small" sx={{ color: 'info.main' }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12}>
                    <Paper elevation={1} sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ mr: 1 }}>
                          Did Pay Push Margin Down?
                        </Typography>
                        <Tooltip title={sectionTooltips.payPushMargin} arrow placement="right">
                          <IconButton size="small" sx={{ color: 'info.main' }}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <ResponsiveContainer width="100%" height={350}>
                        <ComposedChart data={timeSeriesByMetric['Target Margin Over Time'].map((item, idx) => ({
                          date: item.date,
                          margin: item.value,
                          pay: timeSeriesByMetric['Weekly Gross Pay Over Time'][idx]?.value || 0,
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 12 }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                            <YAxis yAxisId="left" tick={{ fontSize: 12 }} label={{ value: 'Margin (%)', angle: -90, position: 'insideLeft' }} />
                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} label={{ value: 'Pay ($)', angle: 90, position: 'insideRight' }} />
                            <RechartsTooltip />
                            <Legend />
                          <Bar yAxisId="right" dataKey="pay" fill={colors.pay} name="Weekly Gross Pay ($)" />
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="margin"
                            stroke={colors.profit}
                            strokeWidth={3}
                            dot={{ r: 5 }}
                            name="Target Margin (%)"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                </Grid>
              </>
            )}

            {/* Rate Analysis - Bill Rate vs Pay Rate */}
            {timeSeriesByMetric['Bill Rate Over Time'] && timeSeriesByMetric['Pay Rate Over Time'] && (
              <>
                <Box display="flex" alignItems="center" sx={{ mt: 4, mb: 2 }}>
                  <Typography variant="h6" sx={{ mr: 1 }}>
                    Rate Analysis
                  </Typography>
                  <Tooltip title={sectionTooltips.rateAnalysis} arrow placement="right">
                    <IconButton size="small" sx={{ color: 'info.main' }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12}>
                    <Paper elevation={1} sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ mr: 1 }}>
                          Bill Rate vs Pay Rate Comparison
                        </Typography>
                        <Tooltip title={sectionTooltips.billPayRateComparison} arrow placement="right">
                          <IconButton size="small" sx={{ color: 'info.main' }}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <ResponsiveContainer width="100%" height={350}>
                        <ComposedChart
                          data={timeSeriesByMetric['Bill Rate Over Time'].map((item, idx) => ({
                            date: item.date,
                            billRate: item.value,
                            payRate: timeSeriesByMetric['Pay Rate Over Time'][idx]?.value || 0,
                            spread: item.value - (timeSeriesByMetric['Pay Rate Over Time'][idx]?.value || 0),
                          }))}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 12 }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                          <YAxis yAxisId="left" tick={{ fontSize: 12 }} label={{ value: 'Rate ($)', angle: -90, position: 'insideLeft' }} />
                          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} label={{ value: 'Spread ($)', angle: 90, position: 'insideRight' }} />
                          <RechartsTooltip />
                          <Legend />
                          <Bar yAxisId="left" dataKey="billRate" fill={colors.rates} name="Bill Rate ($)" />
                          <Bar yAxisId="left" dataKey="payRate" fill={colors.pay} name="Pay Rate ($)" />
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="spread"
                            stroke={colors.profit}
                            strokeWidth={3}
                            dot={{ r: 5 }}
                            name="Spread (Bill - Pay)"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                </Grid>
              </>
            )}

            {/* Financial Performance - Revenue, Gross Profit, Gross Margin */}
            {(timeSeriesByMetric['Revenue Over Time'] || timeSeriesByMetric['Gross Profit Over Time'] || timeSeriesByMetric['Gross Margin Over Time']) && (
              <>
                <Box display="flex" alignItems="center" sx={{ mt: 4, mb: 2 }}>
                  <Typography variant="h6" sx={{ mr: 1 }}>
                    Financial Performance
                  </Typography>
                  <Tooltip title={sectionTooltips.financialPerformance} arrow placement="right">
                    <IconButton size="small" sx={{ color: 'info.main' }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  {timeSeriesByMetric['Revenue Over Time'] && timeSeriesByMetric['Gross Profit Over Time'] && (
                    <Grid item xs={12} md={6}>
                      <Paper elevation={1} sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ mr: 1 }}>
                            Revenue vs Gross Profit
                          </Typography>
                          <Tooltip title={sectionTooltips.revenueVsProfit} arrow placement="right">
                            <IconButton size="small" sx={{ color: 'info.main' }}>
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <ResponsiveContainer width="100%" height={300}>
                          <ComposedChart
                            data={timeSeriesByMetric['Revenue Over Time'].map((item, idx) => ({
                              date: item.date,
                              revenue: item.value,
                              grossProfit: timeSeriesByMetric['Gross Profit Over Time'][idx]?.value || 0,
                            }))}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="date"
                              tick={{ fontSize: 10 }}
                              angle={-45}
                              textAnchor="end"
                              height={60}
                            />
                            <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                            <RechartsTooltip />
                            <Legend />
                            <Area
                              yAxisId="left"
                              type="monotone"
                              dataKey="revenue"
                              fill={colors.financial}
                              fillOpacity={0.3}
                              stroke={colors.financial}
                              name="Revenue ($)"
                            />
                            <Line
                              yAxisId="right"
                              type="monotone"
                              dataKey="grossProfit"
                              stroke={colors.profit}
                              strokeWidth={3}
                              dot={{ r: 4 }}
                              name="Gross Profit ($)"
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </Paper>
                    </Grid>
                  )}
                  {timeSeriesByMetric['Gross Margin Over Time'] && (
                    <Grid item xs={12} md={6}>
                      <Paper elevation={1} sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ mr: 1 }}>
                            Gross Margin Trend
                          </Typography>
                          <Tooltip title={sectionTooltips.grossMarginTrend} arrow placement="right">
                            <IconButton size="small" sx={{ color: 'info.main' }}>
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <ResponsiveContainer width="100%" height={300}>
                          <AreaChart data={timeSeriesByMetric['Gross Margin Over Time']}>
                            <defs>
                              <linearGradient id="colorMargin" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={colors.profit} stopOpacity={0.4} />
                                <stop offset="95%" stopColor={colors.profit} stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="date"
                              tick={{ fontSize: 10 }}
                              angle={-45}
                              textAnchor="end"
                              height={60}
                            />
                            <YAxis tick={{ fontSize: 10 }} label={{ value: 'Margin (%)', angle: -90, position: 'insideLeft' }} />
                            <RechartsTooltip />
                            <Area
                              type="monotone"
                              dataKey="value"
                              stroke={colors.profit}
                              fillOpacity={1}
                              fill="url(#colorMargin)"
                              name="Gross Margin (%)"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </>
            )}

            {/* Cost Components Analysis */}
            {(timeSeriesByMetric['VMS Over Time'] || timeSeriesByMetric['Other Costs Over Time'] || 
              timeSeriesByMetric['Meals Per Diem Over Time'] || timeSeriesByMetric['Lodging Per Diem Over Time']) && (
              <>
                <Box display="flex" alignItems="center" sx={{ mt: 4, mb: 2 }}>
                  <Typography variant="h6" sx={{ mr: 1 }}>
                    Cost Components Analysis
                  </Typography>
                  <Tooltip title={sectionTooltips.costComponents} arrow placement="right">
                    <IconButton size="small" sx={{ color: 'info.main' }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12}>
                    <Paper elevation={1} sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ mr: 1 }}>
                          Cost Components Over Time
                        </Typography>
                        <Tooltip title={sectionTooltips.costComponentsOverTime} arrow placement="right">
                          <IconButton size="small" sx={{ color: 'info.main' }}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <ResponsiveContainer width="100%" height={350}>
                        <ComposedChart
                          data={(timeSeriesByMetric['VMS Over Time'] || timeSeriesByMetric['Other Costs Over Time'] || 
                                timeSeriesByMetric['Meals Per Diem Over Time'] || timeSeriesByMetric['Lodging Per Diem Over Time'] || [])
                            .map((item, idx) => ({
                              date: item.date,
                              vms: timeSeriesByMetric['VMS Over Time']?.[idx]?.value || 0,
                              other: timeSeriesByMetric['Other Costs Over Time']?.[idx]?.value || 0,
                              meals: timeSeriesByMetric['Meals Per Diem Over Time']?.[idx]?.value || 0,
                              lodging: timeSeriesByMetric['Lodging Per Diem Over Time']?.[idx]?.value || 0,
                            }))}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 12 }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                          <YAxis tick={{ fontSize: 12 }} label={{ value: 'Cost ($)', angle: -90, position: 'insideLeft' }} />
                          <RechartsTooltip />
                          <Legend />
                          {timeSeriesByMetric['VMS Over Time'] && (
                            <Bar dataKey="vms" fill="#8D6E63" name="VMS" />
                          )}
                          {timeSeriesByMetric['Other Costs Over Time'] && (
                            <Bar dataKey="other" fill="#A1887F" name="Other Costs" />
                          )}
                          {timeSeriesByMetric['Meals Per Diem Over Time'] && (
                            <Bar dataKey="meals" fill="#BCAAA4" name="Meals Per Diem" />
                          )}
                          {timeSeriesByMetric['Lodging Per Diem Over Time'] && (
                            <Bar dataKey="lodging" fill="#D7CCC8" name="Lodging Per Diem" />
                          )}
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                </Grid>
              </>
            )}

            {/* Time Series Charts - Individual Metrics */}
            {Object.keys(timeSeriesByMetric).filter(name => 
              !name.includes('Min') && !name.includes('Max') && 
              name !== 'Weekly Gross Pay Over Time' && name !== 'Target Margin Over Time' &&
              name !== 'Bill Rate Over Time' && name !== 'Pay Rate Over Time' &&
              name !== 'Revenue Over Time' && name !== 'Gross Profit Over Time' &&
              name !== 'Gross Margin Over Time' && name !== 'VMS Over Time' &&
              name !== 'Other Costs Over Time' && name !== 'Meals Per Diem Over Time' &&
              name !== 'Lodging Per Diem Over Time'
            ).length > 0 && (
              <>
                <Box display="flex" alignItems="center" sx={{ mt: 4, mb: 2 }}>
                  <Typography variant="h6" sx={{ mr: 1 }}>
                    Additional Metrics
                  </Typography>
                  <Tooltip title={sectionTooltips.additionalMetrics} arrow placement="right">
                    <IconButton size="small" sx={{ color: 'info.main' }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  {Object.entries(timeSeriesByMetric)
                    .filter(([name]) => 
                      !name.includes('Min') && !name.includes('Max') && 
                      name !== 'Weekly Gross Pay Over Time' && name !== 'Target Margin Over Time' &&
                      name !== 'Bill Rate Over Time' && name !== 'Pay Rate Over Time' &&
                      name !== 'Revenue Over Time' && name !== 'Gross Profit Over Time' &&
                      name !== 'Gross Margin Over Time' && name !== 'VMS Over Time' &&
                      name !== 'Other Costs Over Time' && name !== 'Meals Per Diem Over Time' &&
                      name !== 'Lodging Per Diem Over Time'
                    )
                    .map(([metricName, data]) => (
                      <Grid item xs={12} md={6} key={metricName}>
                        <Paper elevation={1} sx={{ p: 2 }}>
                          <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ mr: 1 }}>
                              {metricName}
                            </Typography>
                            <Tooltip 
                              title={
                                <Box sx={{ p: 1, maxWidth: 400 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    <strong>{metricName}</strong>
                                  </Typography>
                                  <Typography variant="body2">
                                    Time series data showing {metricName.toLowerCase()} over time. Data is calculated from the latest 25 saves.
                                  </Typography>
                                </Box>
                              } 
                              arrow 
                              placement="right"
                            >
                              <IconButton size="small" sx={{ color: 'info.main' }}>
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <ResponsiveContainer width="100%" height={250}>
                            <LineChart data={data}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis
                                dataKey="date"
                                tick={{ fontSize: 10 }}
                                angle={-45}
                                textAnchor="end"
                                height={60}
                              />
                              <YAxis tick={{ fontSize: 10 }} />
                              <RechartsTooltip />
                              <Line
                                type="monotone"
                                dataKey="value"
                                stroke={colors.flexibility}
                                strokeWidth={2}
                                dot={{ r: 3 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </Paper>
                      </Grid>
                    ))}
                </Grid>
              </>
            )}

            {/* Raw History Data Table */}
            {dashboard.historyTable.length > 0 && (
              <>
                <Box display="flex" alignItems="center" sx={{ mt: 4, mb: 2 }}>
                  <Typography variant="h6" sx={{ mr: 1 }}>
                    History Details
                  </Typography>
                  <Tooltip title={sectionTooltips.historyDetails} arrow placement="right">
                    <IconButton size="small" sx={{ color: 'info.main' }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TableContainer component={Paper} elevation={1}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Created Date</TableCell>
                        <TableCell>Weekly Gross Pay</TableCell>
                        <TableCell>Min Weekly Gross</TableCell>
                        <TableCell>Max Weekly Gross</TableCell>
                        <TableCell>Target Margin</TableCell>
                        <TableCell>Start Date</TableCell>
                        <TableCell>End Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dashboard.historyTable.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {new Date(row.createdDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {row.weeklyGrossPay !== null ? `$${row.weeklyGrossPay.toFixed(2)}` : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {row.minWeeklyGrossPay !== null ? `$${row.minWeeklyGrossPay.toFixed(2)}` : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {row.maxWeeklyGrossPay !== null ? `$${row.maxWeeklyGrossPay.toFixed(2)}` : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {row.targetMargin !== null ? `${row.targetMargin}%` : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {new Date(row.startDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {new Date(row.endDate).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        )}

        {!loading && !error && (!dashboard || dashboard.kpis.length === 0) && (
          <Box p={3}>
            <Typography>No scenario history data available.</Typography>
          </Box>
        )}
    </GenericDialog>
  );
};

